import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Markdown from '../components/Markdown';

// Standalone, unauthenticated docs viewer. Uses a raw axios instance (not the
// shared api client) so the global 401 -> /login redirect never fires here.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const http = axios.create({ baseURL: API_BASE });

// Uploaded project logos are stored as /uploads/… paths relative to the API
// host; white-label site logos are already absolute URLs.
const assetUrl = (p) => {
  if (!p) return '';
  if (/^(https?:)?\/\//i.test(p) || p.startsWith('data:')) return p;
  return `${API_BASE.replace(/\/api\/?$/, '')}/${p.replace(/^\//, '')}`;
};

const METHOD_COLORS = {
  GET: '#22c55e', POST: '#3b82f6', PUT: '#f59e0b', PATCH: '#a855f7', DELETE: '#ef4444', HEAD: '#6b7280', OPTIONS: '#6b7280',
};
const LANGS = ['cURL', 'JavaScript', 'Python'];

// Guide pages share the :endpointSlug route param, so their slugs are
// namespaced to keep them from colliding with endpoint slugs.
const PAGE_PREFIX = 'guide-';
const pageSlugFor = (page) => `${PAGE_PREFIX}${page.slug}`;
const findPageBySlug = (pages, slug) =>
  (slug && slug.startsWith(PAGE_PREFIX)
    ? pages.find(p => p.slug === slug.slice(PAGE_PREFIX.length)) || null
    : null);

function endpointSlugFor(ep) {
  const name = (ep.name || 'endpoint').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${name}-${(ep.id || '').replace(/-/g, '').slice(0, 10)}`;
}
function findEndpointBySlug(endpoints, slug) {
  if (!slug) return null;
  const frag = slug.split('-').pop();
  return endpoints.find(ep => ep.id && ep.id.replace(/-/g, '').startsWith(frag))
      || endpoints.find(ep => endpointSlugFor(ep) === slug) || null;
}
function pretty(json) { try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json; } }
const hasBody = (ep) => ['POST', 'PUT', 'PATCH'].includes(ep.method) && !!ep.body?.example_json;

function buildUrl(ep, baseUrl) {
  let path = ep.path || '';
  (ep.params || []).filter(p => p.param_in === 'path').forEach(p => { path = path.replace(`{${p.name}}`, p.example_value || `:${p.name}`); });
  const qs = (ep.params || []).filter(p => p.param_in === 'query' && p.example_value)
    .map(p => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.example_value)}`).join('&');
  return `${(baseUrl || '').replace(/\/+$/, '')}${path}${qs ? `?${qs}` : ''}`;
}
function headerObj(ep) {
  const h = {};
  (ep.params || []).filter(p => p.param_in === 'header' && p.example_value).forEach(p => { h[p.name] = p.example_value; });
  if (hasBody(ep)) h['Content-Type'] = ep.body.content_type || 'application/json';
  return h;
}
function genSnippet(ep, baseUrl, lang) {
  const url = buildUrl(ep, baseUrl);
  const h = headerObj(ep);
  const body = hasBody(ep) ? pretty(ep.body.example_json) : null;
  if (lang === 'cURL') {
    const lines = [`curl -X ${ep.method} "${url}"`];
    Object.entries(h).forEach(([k, v]) => lines.push(`  -H "${k}: ${v}"`));
    if (body) lines.push(`  -d '${body.replace(/\n\s*/g, '')}'`);
    return lines.join(' \\\n');
  }
  if (lang === 'JavaScript') {
    const opts = [`  method: "${ep.method}"`];
    if (Object.keys(h).length) opts.push(`  headers: ${JSON.stringify(h, null, 2).replace(/\n/g, '\n  ')}`);
    if (body) opts.push(`  body: JSON.stringify(${body})`);
    return `const res = await fetch("${url}", {\n${opts.join(',\n')}\n});\nconst data = await res.json();\nconsole.log(data);`;
  }
  // Python
  const lines = ['import requests', ''];
  if (Object.keys(h).length) lines.push(`headers = ${JSON.stringify(h, null, 2)}`);
  if (body) lines.push(`payload = ${body}`);
  const args = [`"${url}"`];
  if (Object.keys(h).length) args.push('headers=headers');
  if (body) args.push('json=payload');
  lines.push('', `response = requests.${ep.method.toLowerCase()}(${args.join(', ')})`, 'print(response.json())');
  return lines.join('\n');
}

export default function PublicDocsPage() {
  const { slug, token, endpointSlug } = useParams();
  const navigate = useNavigate();
  const docBase = slug ? `/docs/${slug}` : `/share/${token}`;

  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [search, setSearch] = useState('');

  const fetchDocs = useCallback(async (pwd) => {
    setLoading(true); setError(null);
    try {
      const url = slug ? `/public/docs/${slug}` : `/public/share/${token}${pwd ? `?password=${encodeURIComponent(pwd)}` : ''}`;
      const res = await http.get(url);
      if (res.data.requires_password) { setNeedsPassword(true); setLoading(false); return; }
      setNeedsPassword(false); setDocs(res.data);
    } catch (err) {
      const authToken = slug ? localStorage.getItem('token') : null;
      if (authToken) {
        try {
          const res = await http.get(`/publish-preview/${slug}`, { headers: { Authorization: `Bearer ${authToken}` } });
          setDocs(res.data); setLoading(false); return;
        } catch { /* fall through */ }
      }
      setError(err.response?.data?.error || 'Failed to load documentation');
    } finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const endpoints = useMemo(() => docs?.endpoints || [], [docs]);
  const pages = useMemo(() => docs?.pages || [], [docs]);

  // Guide sections, in the order the API returned them
  const sections = useMemo(() => {
    const bySection = new Map();
    pages.forEach(p => {
      const name = p.section || 'Getting Started';
      if (!bySection.has(name)) bySection.set(name, []);
      bySection.get(name).push(p);
    });
    return [...bySection.entries()].map(([name, items]) => ({ name, items }));
  }, [pages]);

  const selectedPage = useMemo(() => findPageBySlug(pages, endpointSlug), [pages, endpointSlug]);
  const selected = useMemo(
    () => (selectedPage ? null : (endpointSlug && findEndpointBySlug(endpoints, endpointSlug)) || endpoints[0] || null),
    [endpoints, endpointSlug, selectedPage],
  );

  // Keep the URL pointed at whatever is open (so links are always shareable).
  // With no slug in the URL, guides lead — a reader should land on the
  // introduction rather than an arbitrary endpoint.
  useEffect(() => {
    if (endpointSlug) return;
    if (pages.length) navigate(`${docBase}/${pageSlugFor(pages[0])}`, { replace: true });
    else if (selected) navigate(`${docBase}/${endpointSlugFor(selected)}`, { replace: true });
  }, [pages, selected, endpointSlug, docBase, navigate]);

  if (loading) return <Centered>Loading documentation…</Centered>;

  if (needsPassword) {
    return (
      <Centered>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 28, width: 320, textAlign: 'center', boxShadow: '0 4px 24px #0000000d' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <h2 style={{ fontSize: 16, margin: '0 0 4px', color: '#111827' }}>Password required</h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>This documentation is protected.</p>
          <form onSubmit={e => { e.preventDefault(); fetchDocs(password); }}>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus placeholder="Enter password"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }} />
            <button type="submit" style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#5865f2', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>View docs</button>
          </form>
        </div>
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
          <h2 style={{ fontSize: 18, color: '#111827', margin: '0 0 4px' }}>{error}</h2>
          <p style={{ fontSize: 13, color: '#6b7280' }}>The documentation you're looking for isn't available.</p>
        </div>
      </Centered>
    );
  }

  const { site, project, folders = [], schemas = [], preview, status } = docs;
  const accent = site?.nav_color || '#5865f2';
  const filtered = search
    ? endpoints.filter(e => `${e.name} ${e.path} ${e.method}`.toLowerCase().includes(search.toLowerCase()))
    : endpoints;

  const go = (ep) => navigate(`${docBase}/${endpointSlugFor(ep)}`);
  const goPage = (page) => navigate(`${docBase}/${pageSlugFor(page)}`);

  const filteredSections = search
    ? sections
      .map(s => ({ ...s, items: s.items.filter(p => p.title.toLowerCase().includes(search.toLowerCase())) }))
      .filter(s => s.items.length)
    : sections;

  return (
    <div style={S.page}>
      {preview && status !== 'published' && (
        <div style={S.previewBar}>🔒 Owner preview — this site is <strong>{status || 'draft'}</strong> and not publicly visible yet. Publish it to share this URL.</div>
      )}

      {/* Top bar */}
      <header style={{ ...S.topbar, borderTopColor: accent }}>
        {site?.logo_url || project?.logo_url
          ? <img src={assetUrl(site?.logo_url || project.logo_url)} alt="" style={{ height: 26, maxWidth: 160, objectFit: 'contain', borderRadius: 5 }} />
          : <div style={{ ...S.logoBox, background: accent }}>📘</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.topTitle}>{site?.site_title || site?.name || project.name}</div>
          {project.base_url && <code style={S.topUrl}>{project.base_url}</code>}
        </div>
        <span style={S.poweredTop}>API Documentation</span>
      </header>

      <div style={S.body}>
        {/* Left nav */}
        <nav style={S.sidebar}>
          <div style={S.searchWrap}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search endpoints…" style={S.search} />
          </div>
          <div style={S.navScroll}>
            {filteredSections.map(section => (
              <div key={section.name} style={{ marginBottom: 14 }}>
                <div style={S.navGroupLabel}>{section.name}</div>
                {section.items.map(page => (
                  <div key={page.id}
                    onClick={() => goPage(page)}
                    style={{
                      ...S.pageItem,
                      ...(selectedPage?.id === page.id ? { background: `${accent}14`, color: accent, fontWeight: 600 } : {}),
                    }}>
                    {page.title}
                  </div>
                ))}
              </div>
            ))}
            <SidebarTree folders={folders} endpoints={filtered} selectedId={selected?.id} accent={accent} onSelect={go} />
            {schemas.length > 0 && (
              <>
                <div style={S.navGroupLabel}>Schemas</div>
                {schemas.map(s => (
                  <div key={s.id} style={S.schemaItem}>{s.name}</div>
                ))}
              </>
            )}
          </div>
        </nav>

        {/* Center: endpoint detail */}
        <main style={S.center}>
          {selectedPage && (
            <article style={S.guide}>
              <div style={{ ...S.guideKicker, color: accent }}>{selectedPage.section}</div>
              <h1 style={S.guideTitle}>{selectedPage.title}</h1>
              <Markdown content={selectedPage.content} variant="light" />
            </article>
          )}
          {!selected && !selectedPage && <div style={{ color: '#6b7280', padding: 40 }}>No endpoints to display yet.</div>}
          {selected && <EndpointDetail ep={selected} baseUrl={project.base_url} deepLink={`${window.location.origin}${docBase}/${endpointSlugFor(selected)}`} />}
        </main>

        {/* Right: code samples + run */}
        {selected && <RightPanel ep={selected} baseUrl={project.base_url} accent={accent}
          onTest={(ep, inputs) => runTest(ep, inputs, { preview, slug, token, password, project })} />}
      </div>
    </div>
  );
}

// Dispatch a live request: published docs -> public proxy; owner preview -> authed proxy.
async function runTest(ep, { pathParams, queryParams, headers, body }, ctx) {
  const { preview, slug, token, password, project } = ctx;
  let parsedBody = body;
  if (body && typeof body === 'string') { try { parsedBody = JSON.parse(body); } catch { /* raw */ } }

  if (preview) {
    let path = ep.path;
    Object.entries(pathParams).forEach(([k, v]) => { if (v) path = path.replace(`{${k}}`, encodeURIComponent(v)); });
    const url = `${(project.base_url || '').replace(/\/+$/, '')}${path}`;
    const authToken = localStorage.getItem('token');
    const res = await http.post('/send-request',
      { url, method: ep.method, headers, params: queryParams, body: parsedBody },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {});
    return res.data;
  }
  const endpoint = slug
    ? `/public/docs/${slug}/try`
    : `/public/share/${token}/try${password ? `?password=${encodeURIComponent(password)}` : ''}`;
  const res = await http.post(endpoint, { endpoint_id: ep.id, path_params: pathParams, query_params: queryParams, headers, body: parsedBody });
  return res.data;
}

function SidebarTree({ folders, endpoints, selectedId, accent, onSelect }) {
  const [open, setOpen] = useState({});
  const ungrouped = endpoints.filter(e => !e.folder_id);
  const Item = (ep) => (
    <button key={ep.id} onClick={() => onSelect(ep)} style={{ ...S.navItem, background: selectedId === ep.id ? `${accent}14` : 'transparent', color: selectedId === ep.id ? '#111827' : '#374151' }}>
      <span style={{ ...S.navMethod, color: METHOD_COLORS[ep.method] }}>{ep.method}</span>
      <span style={S.navName}>{ep.name}</span>
    </button>
  );
  return (
    <>
      {folders.map(f => {
        const eps = endpoints.filter(e => e.folder_id === f.id);
        if (!eps.length) return null;
        const isOpen = open[f.id] !== false;
        return (
          <div key={f.id}>
            <button onClick={() => setOpen(o => ({ ...o, [f.id]: !isOpen }))} style={S.folderRow}>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>{isOpen ? '▾' : '▸'}</span>
              <span>{f.name}</span>
              <span style={S.folderCount}>{eps.length}</span>
            </button>
            {isOpen && eps.map(Item)}
          </div>
        );
      })}
      {ungrouped.map(Item)}
      {endpoints.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: '#9ca3af' }}>No matches</div>}
    </>
  );
}

function EndpointDetail({ ep, baseUrl, deepLink }) {
  const [copied, setCopied] = useState(false);
  const fullPath = `${baseUrl || ''}${ep.path}`;
  const groups = ['path', 'query', 'header', 'cookie'].map(g => ({ g, items: (ep.params || []).filter(p => p.param_in === g) })).filter(x => x.items.length);
  const copy = () => { navigator.clipboard?.writeText(deepLink); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{ep.name}</h1>
        {ep.status && <span style={S.statusPill}>{ep.status}</span>}
        <button onClick={copy} style={{ ...S.linkBtn, color: copied ? '#16a34a' : '#6b7280' }}>{copied ? '✓ Copied' : '🔗 Copy link'}</button>
      </div>

      <div style={S.pathRow}>
        <span style={{ ...S.methodBadge, background: METHOD_COLORS[ep.method] }}>{ep.method}</span>
        <code style={S.pathCode}>{fullPath}</code>
      </div>

      {ep.description && <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}>{ep.description}</p>}

      {groups.map(({ g, items }) => (
        <Section key={g} title={`${g[0].toUpperCase() + g.slice(1)} parameters`}>
          <table style={S.table}>
            <thead><tr>{['Name', 'Type', 'Required', 'Description'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td style={S.td}><code style={S.codeInline}>{p.name}</code></td>
                  <td style={S.td}><span style={{ color: '#a855f7' }}>{p.type}</span></td>
                  <td style={S.td}>{p.required ? <span style={{ color: '#ef4444', fontWeight: 600 }}>required</span> : <span style={{ color: '#9ca3af' }}>optional</span>}</td>
                  <td style={S.td}>{p.description || '—'}{p.example_value ? <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>e.g. {p.example_value}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ))}

      {ep.body?.example_json && (
        <Section title="Request body" hint={ep.body.content_type}>
          <CodeBlock>{pretty(ep.body.example_json)}</CodeBlock>
        </Section>
      )}

      {ep.responses?.length > 0 && (
        <Section title="Responses"><ResponseTabs responses={ep.responses} /></Section>
      )}
    </div>
  );
}

function ResponseTabs({ responses }) {
  const [active, setActive] = useState(0);
  const r = responses[active];
  const ok = String(r.status_code).startsWith('2');
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {responses.map((rr, i) => (
          <button key={rr.id} onClick={() => setActive(i)}
            style={{ ...S.respTab, ...(i === active ? { background: '#111827', color: '#fff', borderColor: '#111827' } : {}), color: i === active ? '#fff' : (String(rr.status_code).startsWith('2') ? '#16a34a' : '#dc2626') }}>
            {rr.status_code}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: ok ? '#16a34a' : '#dc2626' }}>{r.name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>{r.content_type}</span></div>
      {r.example_json && <CodeBlock>{pretty(r.example_json)}</CodeBlock>}
    </div>
  );
}

function RightPanel({ ep, baseUrl, accent, onTest }) {
  const [lang, setLang] = useState('cURL');
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('code'); // 'code' | 'run'
  const snippet = genSnippet(ep, baseUrl, lang);
  const copy = () => { navigator.clipboard?.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <aside style={S.right}>
      <div style={S.rightTabs}>
        {[['code', 'Sample code'], ['run', 'Try it out']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ ...S.rightTab, ...(tab === id ? { color: '#fff', borderBottomColor: accent } : {}) }}>{label}</button>
        ))}
      </div>

      {tab === 'code' && (
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {LANGS.map(l => (
              <button key={l} onClick={() => setLang(l)} style={{ ...S.langBtn, ...(lang === l ? { background: accent, color: '#fff', borderColor: accent } : {}) }}>{l}</button>
            ))}
            <button onClick={copy} style={{ ...S.langBtn, marginLeft: 'auto', color: copied ? '#16a34a' : '#cbd5e1' }}>{copied ? '✓' : 'Copy'}</button>
          </div>
          <pre style={S.darkCode}>{snippet}</pre>
        </div>
      )}

      {tab === 'run' && <RunPanel ep={ep} accent={accent} onTest={onTest} />}
    </aside>
  );
}

function RunPanel({ ep, accent, onTest }) {
  const pathP = (ep.params || []).filter(p => p.param_in === 'path');
  const queryP = (ep.params || []).filter(p => p.param_in === 'query');
  const headerP = (ep.params || []).filter(p => p.param_in === 'header');
  const body = hasBody(ep);

  const seed = (list) => Object.fromEntries(list.map(p => [p.name, p.example_value || '']));
  const [pathVals, setPathVals] = useState(() => seed(pathP));
  const [queryVals, setQueryVals] = useState(() => seed(queryP));
  const [headerVals, setHeaderVals] = useState(() => seed(headerP));
  const [bodyVal, setBodyVal] = useState(() => (body ? pretty(ep.body.example_json) : ''));
  const [sending, setSending] = useState(false);
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState(null);

  const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null));
  const send = async () => {
    setSending(true); setErr(null); setResp(null);
    try {
      const data = await onTest(ep, { pathParams: clean(pathVals), queryParams: clean(queryVals), headers: clean(headerVals), body: body ? bodyVal : undefined });
      if (data.success === false) setErr(data.error || 'Request failed'); else setResp(data);
    } catch (e) { setErr(e.response?.data?.error || e.message || 'Request failed'); }
    finally { setSending(false); }
  };

  const field = (label, val, on, ph) => (
    <div key={label} style={{ marginBottom: 8 }}>
      <label style={S.runLabel}>{label}</label>
      <input value={val} onChange={e => on(e.target.value)} placeholder={ph || ''} style={S.runInput} />
    </div>
  );

  return (
    <div style={{ padding: 14 }}>
      {pathP.length > 0 && <RunGroup title="Path">{pathP.map(p => field(`${p.name}${p.required ? ' *' : ''}`, pathVals[p.name] || '', v => setPathVals(s => ({ ...s, [p.name]: v })), p.example_value))}</RunGroup>}
      {queryP.length > 0 && <RunGroup title="Query">{queryP.map(p => field(`${p.name}${p.required ? ' *' : ''}`, queryVals[p.name] || '', v => setQueryVals(s => ({ ...s, [p.name]: v })), p.example_value))}</RunGroup>}
      {headerP.length > 0 && <RunGroup title="Headers">{headerP.map(p => field(p.name, headerVals[p.name] || '', v => setHeaderVals(s => ({ ...s, [p.name]: v })), p.example_value))}</RunGroup>}
      {body && <RunGroup title="Body"><textarea value={bodyVal} onChange={e => setBodyVal(e.target.value)} spellCheck={false} style={S.runBody} /></RunGroup>}

      <button onClick={send} disabled={sending} style={{ ...S.sendBtn, background: sending ? '#9ca3af' : accent }}>
        {sending ? 'Sending…' : `Send ${ep.method}`}
      </button>

      {err && <div style={S.runErr}>⚠ {err}</div>}
      {resp && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: resp.status < 400 ? '#16a34a' : '#dc2626' }}>{resp.status} {resp.statusText || ''}</span>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{resp.response_time} ms</span>
          </div>
          <pre style={S.darkCode}>{typeof resp.data === 'object' ? JSON.stringify(resp.data, null, 2) : String(resp.data ?? '')}</pre>
        </div>
      )}
    </div>
  );
}

const RunGroup = ({ title, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);
const Section = ({ title, hint, children }) => (
  <section style={{ marginTop: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #eef0f2' }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>{title}</h3>
      {hint && <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{hint}</span>}
    </div>
    {children}
  </section>
);
const CodeBlock = ({ children }) => <pre style={S.lightCode}>{children}</pre>;
const Centered = ({ children }) => <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: 'Inter, system-ui, sans-serif', color: '#6b7280', fontSize: 14 }}>{children}</div>;

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const S = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', color: '#1f2937', fontFamily: 'Inter, system-ui, sans-serif' },
  previewBar: { background: '#fef3c7', color: '#92400e', textAlign: 'center', padding: '8px 16px', fontSize: 13, fontWeight: 500 },
  topbar: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 22px', borderBottom: '1px solid #eef0f2', borderTop: '3px solid #5865f2', flexShrink: 0 },
  logoBox: { width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 },
  topTitle: { fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  topUrl: { fontSize: 12, color: '#6b7280', fontFamily: MONO },
  poweredTop: { fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  sidebar: { width: 270, flexShrink: 0, borderRight: '1px solid #eef0f2', background: '#fbfbfc', display: 'flex', flexDirection: 'column' },
  searchWrap: { padding: 12, borderBottom: '1px solid #eef0f2' },
  search: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  navScroll: { flex: 1, overflow: 'auto', padding: '8px 8px 24px' },
  navGroupLabel: { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, padding: '14px 10px 6px' },
  navItem: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 7, cursor: 'pointer', marginBottom: 1 },
  navMethod: { fontSize: 9, fontWeight: 800, minWidth: 38, fontFamily: MONO },
  navName: { fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  folderRow: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '7px 8px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#374151' },
  folderCount: { marginLeft: 'auto', fontSize: 10, color: '#9ca3af', background: '#eef0f2', borderRadius: 10, padding: '0 6px' },
  schemaItem: { fontSize: 12.5, color: '#374151', padding: '6px 12px', cursor: 'default' },
  pageItem: { fontSize: 12.5, color: '#374151', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', lineHeight: 1.5 },
  guide: { maxWidth: 760 },
  guideKicker: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
  guideTitle: { fontSize: 30, fontWeight: 800, color: '#111827', margin: '0 0 18px', lineHeight: 1.2 },
  center: { flex: 1, overflow: 'auto', padding: '26px 32px' },
  statusPill: { fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '3px 9px', borderRadius: 5 },
  linkBtn: { marginLeft: 'auto', fontSize: 12, fontWeight: 600, background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '5px 10px', borderRadius: 6, cursor: 'pointer' },
  pathRow: { display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', marginBottom: 16 },
  methodBadge: { fontSize: 11, fontWeight: 800, color: '#fff', padding: '4px 10px', borderRadius: 5, flexShrink: 0 },
  pathCode: { fontSize: 13.5, fontFamily: MONO, color: '#111827', wordBreak: 'break-all' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, color: '#6b7280', fontWeight: 600, padding: '6px 8px', borderBottom: '1px solid #e5e7eb' },
  td: { fontSize: 12.5, color: '#374151', padding: '8px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  codeInline: { fontFamily: MONO, color: '#5865f2', fontSize: 12 },
  respTab: { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontFamily: MONO },
  right: { width: 400, flexShrink: 0, borderLeft: '1px solid #eef0f2', background: '#0f172a', display: 'flex', flexDirection: 'column', overflow: 'auto' },
  rightTabs: { display: 'flex', borderBottom: '1px solid #1e293b', flexShrink: 0 },
  rightTab: { flex: 1, padding: '12px 0', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  langBtn: { fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#cbd5e1', cursor: 'pointer' },
  darkCode: { background: '#0b1220', color: '#e2e8f0', padding: '12px 14px', borderRadius: 8, fontSize: 12, fontFamily: MONO, overflow: 'auto', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  lightCode: { background: '#0f172a', color: '#e2e8f0', padding: '12px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: MONO, overflow: 'auto', margin: 0, lineHeight: 1.6 },
  runLabel: { display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 3 },
  runInput: { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' },
  runBody: { width: '100%', minHeight: 100, padding: '10px 12px', borderRadius: 6, border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', fontFamily: MONO, fontSize: 12.5, boxSizing: 'border-box', resize: 'vertical' },
  sendBtn: { width: '100%', marginTop: 4, padding: '10px', borderRadius: 8, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  runErr: { marginTop: 12, padding: '10px 12px', background: '#7f1d1d', color: '#fecaca', borderRadius: 7, fontSize: 13 },
};
